/**
 * v3.admin — one-shot ETL runbook endpoints.
 * POST /v3/admin/config-seed requires SuperAdmin + one-time bypass key.
 * POST /v3/admin/run-seed-sql runs an arbitrary (admin-approved) SQL batch —
 * see memory `reference_v3_admin_endpoint_pattern.md`.
 */

import type {
  RequestOptions,
  NotifEventBindingUpsert,
  NotifOptOutUpsert,
} from '@retail-ops/shared-types';
import type { ApiHttpClient } from '../http.js';
import { buildAdminAuthHeaders } from '../admin-auth.js';

export interface ConfigSeedRequest {
  /** One-time bypass key (rotated immediately after use). */
  bypassKey: string;
  /** Wave selection — `"all"` runs all four waves; specific wave numbers run subset. */
  wave?: 'all' | 1 | 2 | 3 | 4;
  dryRun?: boolean;
}

export interface ConfigSeedResponse {
  success: boolean;
  wavesRun: ReadonlyArray<number>;
  tablesSeeded: ReadonlyArray<string>;
  rowCounts: Record<string, number>;
  seededAt?: string;
  elapsedMs?: number;
  error?: string;
  errors?: ReadonlyArray<{ table: string; error: string }>;
}

/**
 * Request body for `POST /v3/admin/run-seed-sql`.
 * Provide either `sql` (plain text) or `sqlBase64` (UTF-8 base64) — the latter
 * is preferred when the SQL contains JSON-hostile chars (em-dashes, quoted
 * strings, nested JSON).
 *
 * The `bypassKey` is sent as the `x-config-seed-bypass` header by the client,
 * not in the body.
 */
export interface RunSeedSqlRequest {
  /** Human-readable identifier for this run (shows up in telemetry). */
  name: string;
  /** Plain SQL text. Mutually exclusive with `sqlBase64`. */
  sql?: string;
  /** UTF-8 base64-encoded SQL. Mutually exclusive with `sql`. */
  sqlBase64?: string;
  /** Per-statement timeout (seconds). Defaults to server-side config. */
  statementTimeoutSeconds?: number;
}

export interface RunSeedSqlResponse {
  success: boolean;
  rowsAffected: number;
  batchesExecuted: number;
  elapsedMs?: number;
  error?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Entra picker endpoints (Domain 1.6a — Bill 2026-05-14)
// GET /v3/admin/entra/groups            — generic group picker
// GET /v3/admin/users/entra-candidates  — Add-user autocomplete over the
//                                          identity source group
// Both require Admin role (AdminAuthService gate).
// ─────────────────────────────────────────────────────────────────────────────

export interface EntraGroupCandidate {
  id: string;
  displayName: string;
  mail: string | null;
  mailEnabled: boolean;
  securityEnabled: boolean;
  /** Entra `groupTypes` array — `["Unified"]` indicates Microsoft 365 group. */
  groupTypes: ReadonlyArray<string>;
}

export interface EntraUserCandidate {
  id: string;
  displayName: string;
  userPrincipalName: string;
  mail: string | null;
  givenName: string | null;
  surname: string | null;
  jobTitle: string | null;
  officeLocation: string | null;
}

export interface EntraSearchEnvelope<T> {
  success: boolean;
  data: ReadonlyArray<T>;
  error?: string;
  code?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Domain 1.6c — Entra sync (Bill 2026-05-15).
// Shapes captured live against prod 2026-05-15 — NOT ported from any prior
// SPFx / ROP-teams artifact. Endpoint contracts:
//   POST   /v3/admin/users/entra-sync/diff
//   POST   /v3/admin/users/entra-sync/apply
//   GET    /v3/admin/users/entra-sync/ignore-list
//   POST   /v3/admin/users/entra-sync/ignore
//   POST   /v3/admin/users/entra-sync/ignore-bulk
//   DELETE /v3/admin/users/entra-sync/ignore/{id:long}
// All require the SuperAdmin role today.
// ─────────────────────────────────────────────────────────────────────────────

export interface EntraUserRow {
  entraObjectId: string;
  upn: string;
  mail: string | null;
  displayName: string;
  givenName: string | null;
  surname: string | null;
  jobTitle: string | null;
  department: string | null;
  officeLocation: string | null;
  companyName: string | null;
  mobilePhone: string | null;
  businessPhone: string | null;
  accountEnabled: boolean;
}

export interface EntraSyncFieldDiff {
  fieldName: string;
  oldValue: string | null;
  newValue: string | null;
}

export interface EntraSyncUpdateRow {
  userId: number;
  email: string;
  displayName: string;
  fields: ReadonlyArray<EntraSyncFieldDiff>;
  entra: EntraUserRow;
}

export interface EntraSyncRemoveRow {
  userId: number;
  email: string;
  displayName: string;
  officerCode: string | null;
  branchCode: string | null;
  /** v0.8.x — populated when known so the Global Suggest "Ignore" action
   *  can key the UserGroupSyncIgnores row on (EntraObjectId, GroupId). */
  entraObjectId: string | null;
}

export interface EntraSyncDiffRequest {
  groupObjectId?: string;
  deactivateMissing?: boolean;
}

export interface EntraSyncDiffOutcome {
  adds: ReadonlyArray<EntraUserRow>;
  updates: ReadonlyArray<EntraSyncUpdateRow>;
  removes: ReadonlyArray<EntraSyncRemoveRow>;
  fetchTimingMs: number;
  graphSourceCount: number;
  v3SourceCount: number;
  groupFilter: string | null;
  warnings: ReadonlyArray<string>;
  ignoredCount: number;
  error?: string;
  code?: string;
}

export interface EntraSyncUpdateAccept {
  email: string;
  fieldsToApply: ReadonlyArray<string>;
  entra: EntraUserRow;
}

export interface EntraSyncApplyInput {
  acceptedAdds: ReadonlyArray<EntraUserRow>;
  acceptedUpdates: ReadonlyArray<EntraSyncUpdateAccept>;
  acceptedRemoves: ReadonlyArray<string>;
}

export interface EntraSyncApplyOutcome {
  success: boolean;
  addedCount: number;
  updatedCount: number;
  removedCount: number;
  elapsedMs: number;
  errors: ReadonlyArray<string>;
  error?: string;
  code?: string;
}

export interface EntraIgnoreInput {
  entraObjectId?: string;
  email?: string;
  displayName?: string;
  reason?: string;
}

export interface EntraIgnoreBulkInput {
  rows: ReadonlyArray<EntraIgnoreInput>;
  reason?: string;
}

export interface EntraIgnoreRow {
  id: number;
  entraObjectId: string;
  email: string;
  displayName: string;
  reason: string;
  ignoredByUpn: string;
  ignoredAt: string;
}

export interface EntraIgnoreListResponse {
  success: boolean;
  data: {
    rows: ReadonlyArray<EntraIgnoreRow>;
    count: number;
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// v0.8.0 — Users Entra-extended + Sync rebuild (Bill 2026-05-15)
// docs/admin-panel/users-page-spec-v08.md
// ─────────────────────────────────────────────────────────────────────────────

export interface AuthProbeResponse {
  success: boolean;
  data: {
    bearer: { present: boolean; oid: string | null; upn: string | null };
    officerCodeHeader: string | null;
    resolvedUser: {
      matchedBy: 'oid' | 'officerCode' | 'none' | string;
      userId: number;
      email: string;
      officerCode: string | null;
      entraObjectId: string | null;
      missingOfficerCode: boolean;
      isActive: boolean;
      roles: ReadonlyArray<string>;
    } | null;
  };
}

export interface SingleUserDiffField {
  fieldName: string;
  current: string | null;
  entra: string | null;
}

export interface EntraSnapshot {
  entraObjectId: string;
  upn: string;
  mail: string | null;
  displayName: string;
  givenName: string | null;
  surname: string | null;
  department: string | null;
  jobTitle: string | null;
  officeLocation: string | null;
  mobilePhone: string | null;
  businessPhone: string | null;
  managerOid: string | null;
}

export interface SingleUserDiffResult {
  userId: number;
  email: string;
  entraResolved: boolean;
  entra: EntraSnapshot | null;
  fields: ReadonlyArray<SingleUserDiffField>;
  ignoredFields: ReadonlyArray<string>;
  photoChanged: boolean;
  applied: boolean;
  appliedFieldsCount: number;
  error?: string;
  code?: string;
}

export interface SingleSyncApplyBody {
  acceptedFields: ReadonlyArray<string>;
}

export interface UserPhotoResponse {
  success: boolean;
  data: { userId: number; photoBase64: string | null; photoUpdatedAt: string };
}

export interface ReconcileUserDiff {
  userId: number;
  email: string;
  displayName: string;
  fields: ReadonlyArray<SingleUserDiffField>;
  ignoredFields: ReadonlyArray<string>;
}

export interface ReconcileResult {
  users: ReadonlyArray<ReconcileUserDiff>;
  scannedCount: number;
  withDiffsCount: number;
  error?: string;
  code?: string;
}

export interface GlobalSuggestRoleSection {
  roleCode: string;
  roleName: string;
  groupType: string;
  groupId: string;
  proposeAdds: ReadonlyArray<EntraUserRow>;
  proposeArchives: ReadonlyArray<EntraSyncRemoveRow>;
}

export interface GlobalSuggestResult {
  sections: ReadonlyArray<GlobalSuggestRoleSection>;
  scannedRoles: number;
  ignoredCount: number;
  error?: string;
  code?: string;
}

export interface FieldIgnoreRow {
  ignoreId: number;
  userId: number;
  email: string;
  fieldName: string;
  ignoredAt: string;
  ignoredBy: string | null;
  reason: string | null;
}

export interface FieldIgnoreInput {
  userId: number;
  fieldName: string;
  reason?: string;
}

export interface GroupIgnoreRowV08 {
  ignoreId: string;
  entraObjectId: string;
  groupId: string;
  ignoredAt: string;
  ignoredBy: string | null;
  reason: string | null;
}

export interface GroupIgnoreInputV08 {
  entraObjectId: string;
  groupId: string;
  reason?: string;
}

export interface SubordinateOverrideRow {
  overrideId: number;
  supervisorUserId: number;
  supervisorEmail: string;
  subordinateUserId: number;
  subordinateEmail: string;
  overrideKind: 'add' | 'remove' | string;
  createdAt: string;
  createdBy: string | null;
  reason: string | null;
}

export interface SubordinateOverrideInput {
  supervisorUserId: number;
  subordinateUserId: number;
  overrideKind: 'add' | 'remove';
  reason?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Domain 1.7 — SysOps MissingOfficerCode queue (Bill 2026-05-14)
//   GET  /v3/admin/users/missing-officer-code
//   POST /v3/admin/users/{userId}/resolve-officer-code
// Both require SystemOps (SuperAdmin satisfies).
// ─────────────────────────────────────────────────────────────────────────────

export interface MissingOfficerCodeRow {
  userId: number;
  email: string;
  displayName: string;
  firstName: string | null;
  lastName: string | null;
  officerCode: string | null;
  branchCode: string | null;
  regionCode: string | null;
  updatedAt: string | null;
}

export interface MissingOfficerCodeListResponse {
  success: boolean;
  data: ReadonlyArray<MissingOfficerCodeRow>;
}

export interface ResolveOfficerCodeRequest {
  officerCode: string;
}

export interface ResolveOfficerCodeResponse {
  success: boolean;
  data?: {
    userId: number;
    officerCode: string;
    previousOfficerCode: string | null;
  };
  error?: string;
  code?: 'MISSING_FIELDS' | 'USER_NOT_FOUND' | 'USER_INACTIVE'
    | 'INVALID_OFFICER_CODE' | 'CORE_DIRECTOR_UNREACHABLE'
    | 'CORE_DIRECTOR_ERROR' | string;
}

// ─────────────────────────────────────────────────────────────────────────────
// v0.8.5 — Multi-source Identity Sync, Core Director side (Bill 2026-05-15)
//   POST /v3/admin/users/cd-sync/reconcile
//   POST /v3/admin/users/cd-sync/apply
//   GET  /v3/admin/users/cd-name-mismatch
//   POST /v3/admin/users/{userId}/resolve-cd-name-mismatch
// ─────────────────────────────────────────────────────────────────────────────

export type CdSyncRowType = 'SuggestAssign' | 'Stale' | 'NameMismatch';

export interface CdSyncReconcileRow {
  userId: number;
  email: string;
  displayName: string;
  currentOfficerCode: string | null;
  proposedOfficerCode: string | null;
  cdDescriptor: string | null;
  matchScore: number;
  rowType: CdSyncRowType;
  currentMissingOfficerCode: boolean;
  currentCdNameMismatch: boolean;
}

export interface CdSyncReconcileResponse {
  success: boolean;
  data?: {
    rows: ReadonlyArray<CdSyncReconcileRow>;
    cdOperatorCount: number;
    scannedUserCount: number;
    error?: string;
    code?: string;
  };
  error?: string;
  code?: 'CD_FETCH_FAILED' | 'CD_PARSE_FAILED' | 'CD_FETCH_EXCEPTION' | string;
}

export interface CdSyncApplyInput {
  assigns: ReadonlyArray<{
    userId: number;
    officerCode: string;
    flagNameMismatch: boolean;
  }>;
  staleNullouts: ReadonlyArray<number>;
  nameMismatchAcks: ReadonlyArray<number>;
}

export interface CdSyncApplyResponse {
  success: boolean;
  data?: {
    assignedCount: number;
    staleNulledCount: number;
    nameMismatchFlaggedCount: number;
    errors: ReadonlyArray<string>;
  };
}

export interface CdNameMismatchRow {
  userId: number;
  email: string;
  displayName: string;
  officerCode: string | null;
  updatedAt: string | null;
  cdDescriptor: string | null;
}

export interface CdNameMismatchListResponse {
  success: boolean;
  data: ReadonlyArray<CdNameMismatchRow>;
  /**
   * v0.9.4 — if CD enrichment failed at list-time, the reason surfaces here
   * so SysOps can see WHY descriptors are empty instead of just "(catalog
   * unreachable)". Null/absent when CD fetch succeeded.
   */
  cdFetchError?: string | null;
}

export interface ResolveCdNameMismatchResponse {
  success: boolean;
  data?: { userId: number };
  error?: string;
  code?: 'USER_NOT_FOUND' | string;
}

// ─────────────────────────────────────────────────────────────────────────────
// v0.9.0 — Per-user product-category enablement (Bill 2026-05-15).
//   GET  /v3/admin/users/{userId}/product-categories
//   POST /v3/admin/users/{userId}/product-categories
// Categories: DDA, SAV, CD, IRA, BOX. A row's existence means enabled.
// Default-on-Officer-grant + cascade-on-Officer-revoke happen server-side.
// jXchange proxy gate: ungated operations pass through; mapped ones
// require the caller's UserProductCategories to include the mapped code.
// ─────────────────────────────────────────────────────────────────────────────

export type ProductCategoryCode = 'DDA' | 'SAV' | 'CD' | 'IRA' | 'BOX';

export interface UserProductCategoryRow {
  categoryCode: string;
  updatedAt: string;
}

export interface UserProductCategoriesResponse {
  success: boolean;
  data: ReadonlyArray<UserProductCategoryRow>;
}

/**
 * 2026-10-07 — one row of dbo.UserAccessAudit: who changed a person's access,
 * when, old -> new. `action` is RoleGranted | RoleRemoved | UserCreated |
 * UserUpdated | UserDeleted | ProductCategoriesChanged.
 */
export interface UserAccessAuditRow {
  auditId: number;
  changedAt: string;
  changedBy: string;
  action: string;
  field: string | null;
  oldValue: string | null;
  newValue: string | null;
  source: string | null;
}

export interface UserAccessHistoryResponse {
  success: boolean;
  data: ReadonlyArray<UserAccessAuditRow>;
  error?: string;
  code?: string;
}

export interface UserProductCategoriesReplaceInput {
  categoryCodes: ReadonlyArray<string>;
}

export interface UserProductCategoriesReplaceResponse {
  success: boolean;
  error?: string;
  code?: 'OFFICER_REQUIRED' | string;
}

// v0.9.0 — CdNameMismatch queue row enriched with live CD descriptor.
// (Replaces the simpler shape in the v0.8.5 type.)
export interface CdNameMismatchRowV09 {
  userId: number;
  email: string;
  displayName: string;
  officerCode: string | null;
  updatedAt: string | null;
  cdDescriptor: string | null;
}

// v0.9.3 — Operator Catalog Issues queue (raised by catalog sync).
// v0.9.6 — IncompleteRow REMOVED (Bill 2026-05-15): PCB's catalog has
// legitimate code-operator rows without name/email; flagging them all
// generated 145 false positives. The Resolve flow now tolerates
// incomplete SP rows directly. Historical IncompleteRow rows
// auto-resolve on the next sync (not re-detected → auto-resolved).
export type OperatorCatalogIssueKind =
  | 'DuplicateInitials'
  | 'NameMismatch'
  | 'TitleMismatch'
  | 'NetworkIdMismatch';

export interface OperatorCatalogIssueRow {
  issueId: number;
  kind: string;
  operatorInitials: string | null;
  emailAddress: string | null;
  spValue: string | null;
  authoritativeValue: string | null;
  detail: string | null;
  firstSeenAt: string;
  updatedAt: string;
}

export interface OperatorCatalogIssuesListResponse {
  success: boolean;
  data: ReadonlyArray<OperatorCatalogIssueRow>;
}

export interface ResolveOperatorCatalogIssueResponse {
  success: boolean;
  data?: { issueId: number };
  error?: string;
  code?: 'NOT_FOUND' | string;
}

// ─────────────────────────────────────────────────────────────────────────────
// v0.9.1 — SharePoint-backed Core Operator Catalog (Bill 2026-05-15).
//   POST /v3/admin/core-operator-catalog/refresh  (SystemOps)
//   GET  /v3/admin/core-operator-catalog          (SystemOps)
// CD doesn't expose the broader operator list; PCB maintains the canonical
// roster in a SharePoint list. The Function MI pulls it on-demand + nightly.
// ResolveOfficerCode validates the typed initials against this cached
// catalog rather than CD's officer-only OffCode subset.
// ─────────────────────────────────────────────────────────────────────────────

export interface CoreOperatorCatalogRow {
  operatorInitials: string;
  operatorNumber: string | null;
  operatorName: string | null;
  operatorTitle: string | null;
  networkId: string | null;
  emailAddress: string | null;
  lastSyncedAt: string;
}

export interface CoreOperatorCatalogListResponse {
  success: boolean;
  data: ReadonlyArray<CoreOperatorCatalogRow>;
}

export interface CoreOperatorCatalogRefreshResponse {
  success: boolean;
  error?: string;
  code?: 'SP_SITE_NOT_FOUND' | 'SYNC_EXCEPTION' | string;
  // v0.9.8 (Bill 2026-05-16) — shape changed when dbo.CoreOperatorCatalog
  // was dropped in favor of in-memory snapshot. inserted/updated/retired
  // no longer make sense (cache is replaced wholesale). New fields
  // describe what was loaded + what data-integrity issues fired.
  data?: {
    fetched: number;
    cached: number;
    duplicateGroups: number;
    issuesRaised: number;
    issuesAutoResolved: number;
    elapsedMs: number;
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// IRA admin write surfaces (Wave 5.4 — IRA-ADMIN-AND-RCP-SCAFFOLD)
// Routes: POST /v3/admin/ira-transaction-codes        (createOrUpdate)
//         DELETE /v3/admin/ira-transaction-codes/{scenario}/{sequenceIndex}
//         POST /v3/admin/ira-institution-relationships
//         DELETE /v3/admin/ira-institution-relationships/{code}
//         POST /v3/admin/ira-contribution-phaseouts
//         DELETE /v3/admin/ira-contribution-phaseouts/{taxYear}/{planCode}/{filingStatus}
// All routes require `x-config-seed-bypass: <bypassKey>` header.
// ─────────────────────────────────────────────────────────────────────────────

export interface IraTransactionCodeUpsertBody {
  scenario: string;
  sequenceIndex: number;
  transactionCode: string;
  displayName: string;
  description?: string | null;
  requiresAdminReview: boolean;
  isActive: boolean;
}

export interface IraInstitutionRelationshipUpsertBody {
  code: string;
  displayName: string;
  relationshipType: string;
  penaltyExempt: boolean;
  requiresAdditionalApproval: boolean;
  notes?: string | null;
  isActive: boolean;
}

export interface IraContributionPhaseoutUpsertBody {
  taxYear: number;
  planCode: string;
  filingStatus: string;
  /** Decimal carried as string to preserve precision over the wire. */
  magiPhaseoutStart: string;
  magiPhaseoutEnd: string;
  notes?: string | null;
  isActive: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// System / misc admin write surfaces (Wave 6.1 — WRITE-ROUTES-SYSTEM-MISC)
// Routes (12): SystemSettings, KillListEntries, SlaTargets, CdPenaltyRules,
//              IraReasonCodes, IraContributionLimits — POST upsert + DELETE.
// All routes require `x-config-seed-bypass: <bypassKey>` header.
// ─────────────────────────────────────────────────────────────────────────────

export interface SystemSettingUpsertBody {
  settingKey: string;
  /** Stored as nvarchar(max). null clears value; undefined leaves untouched on update. */
  settingValue?: string | null;
  dataType?: string;
  categoryCode?: string;
  isSensitive?: boolean;
  description?: string | null;
  defaultValue?: string | null;
  isActive?: boolean;
}

export interface KillListEntryUpsertBody {
  /** Omit/empty for create. Provide GUID to update. */
  killListEntryId?: string | null;
  entryType: string;
  targetId: string;
  /** Core Director record type: CIF|DDA|SAV|CD|IRA|BOX|NT|ATM. */
  targetType?: string | null;
  /** Full name of the primary CIF on the account, or of the CIF itself. */
  customerName?: string | null;
  reason: string;
  status?: string;
  retentionHold?: boolean;
  createdBy?: string | null;
}

export interface SlaTargetUpsertBody {
  originCode: string;
  stageCode: string;
  displayName: string;
  targetSeconds: number;
  alertThresholdPercent: number;
  isActive?: boolean;
}

export interface CdPenaltyRuleUpsertBody {
  ruleCode: string;
  displayName: string;
  termMinMonths: number;
  termMaxMonths: number;
  penaltyMonths: number;
  sortOrder?: number | null;
  isActive?: boolean;
}

export interface IraReasonCodeUpsertBody {
  code: string;
  displayName: string;
  form1099Box7Code?: string;
  form5498Box?: string | null;
  /** 0..255 (tinyint). */
  minAge?: number | null;
  maxAge?: number | null;
  requiresExceptionDocumentation?: boolean;
  withholdingDefaultFedPct?: number | null;
  withholdingDefaultStatePct?: number | null;
  isActive?: boolean;
  sortOrder?: number;
  notes?: string | null;
}

export interface IraContributionLimitUpsertBody {
  taxYear: number;
  planCode: string;
  /** Decimal currency value. */
  regularLimit: number;
  catchUpLimit: number;
  catchUpAgeThreshold?: number;
  notes?: string | null;
  isActive?: boolean;
}

/** BACKLOG-1757: one batch of the Kill List "Refresh from Core Director". */
export interface KillListCoreStatusBatch {
  checked: number;
  gone: number;
  marked: number;
  notMarked: number;
  inconclusive: number;
  /** Entries this run still owes a check; repeat with the same runStartedAt until 0. */
  remaining: number;
  /** UTC ISO start of the run; send it back on the next batch. */
  runStartedAt: string;
}

export interface KillListCoreStatusResult {
  success: boolean;
  data?: KillListCoreStatusBatch;
  error?: string;
  code?: string;
}

export interface AdminWriteResult {
  success: boolean;
  data?: unknown;
  error?: string;
  code?: string;
}

class IraTransactionCodesAdminApi {
  public constructor(private readonly http: ApiHttpClient) {}

  public upsert(
    bypassKey: string,
    body: IraTransactionCodeUpsertBody,
    opts?: RequestOptions
  ): Promise<AdminWriteResult> {
    const headers = buildAdminAuthHeaders(opts?.headers, bypassKey);
    return this.http.post<AdminWriteResult>('/v3/admin/ira-transaction-codes', {
      ...opts,
      headers,
      body,
    });
  }

  public remove(
    bypassKey: string,
    scenario: string,
    sequenceIndex: number,
    opts?: RequestOptions
  ): Promise<AdminWriteResult> {
    const headers = buildAdminAuthHeaders(opts?.headers, bypassKey);
    const path = `/v3/admin/ira-transaction-codes/${encodeURIComponent(scenario)}/${encodeURIComponent(
      String(sequenceIndex)
    )}`;
    return this.http.del<AdminWriteResult>(path, { ...opts, headers });
  }
}

class IraInstitutionRelationshipsAdminApi {
  public constructor(private readonly http: ApiHttpClient) {}

  public upsert(
    bypassKey: string,
    body: IraInstitutionRelationshipUpsertBody,
    opts?: RequestOptions
  ): Promise<AdminWriteResult> {
    const headers = buildAdminAuthHeaders(opts?.headers, bypassKey);
    return this.http.post<AdminWriteResult>('/v3/admin/ira-institution-relationships', {
      ...opts,
      headers,
      body,
    });
  }

  public remove(
    bypassKey: string,
    code: string,
    opts?: RequestOptions
  ): Promise<AdminWriteResult> {
    const headers = buildAdminAuthHeaders(opts?.headers, bypassKey);
    return this.http.del<AdminWriteResult>(
      `/v3/admin/ira-institution-relationships/${encodeURIComponent(code)}`,
      { ...opts, headers }
    );
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Wave 6.1 WRITE-ROUTES-REFERENCE — typed admin write routes for the
// 5 Reference-domain catalogs. Routes mirror Wave 5.4 IRA-ADMIN auth posture
// (x-config-seed-bypass header). See AdminReferenceFunction.cs.
// ─────────────────────────────────────────────────────────────────────────────

export interface BranchUpsertBody {
  branchCode: string;
  branchName: string;
  regionCode: string;
  addressLine1?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  phone?: string | null;
  fax?: string | null;
  timeZone?: string | null;
  isKioskEnabled?: boolean;
  hasSdb?: boolean;
  isActive?: boolean;
  /** Branch-specific Entrust launch URL (WT-BACKLOG B1). Null/blank clears it. */
  entrustUrl?: string | null;
}

export interface RegionUpsertBody {
  regionCode: string;
  regionName: string;
  sortOrder?: number | null;
  isActive?: boolean;
  /** Branch codes to flip to this region in the same call. Empty / omitted = no reassignments. */
  branchCodesToReassign?: ReadonlyArray<string>;
}

export interface CoreDirectorCodeUpsertBody {
  domain: string;
  code: string;
  label: string;
  description?: string | null;
  customerType?: string | null;
  notes?: string | null;
  externalRef?: string | null;
  sortOrder?: number | null;
  isActive?: boolean;
}

export interface NaicsCodeUpsertBody {
  code: string;
  title: string;
  depth?: number;
  sectorCode: string;
  parentCode?: string | null;
  isActive?: boolean;
}

export interface StateGovSearchLinkUpsertBody {
  stateCode: string;
  searchType: string;
  searchUrl: string;
  displayLabel: string;
  notes?: string | null;
  isActive?: boolean;
  /** ISO-8601 yyyy-MM-dd or full date string. */
  lastValidated?: string | null;
}

class BranchesAdminApi {
  public constructor(private readonly http: ApiHttpClient) {}

  public upsert(
    bypassKey: string,
    body: BranchUpsertBody,
    opts?: RequestOptions
  ): Promise<AdminWriteResult> {
    const headers = buildAdminAuthHeaders(opts?.headers, bypassKey);
    return this.http.post<AdminWriteResult>('/v3/admin/branches', { ...opts, headers, body });
  }

  public remove(
    bypassKey: string,
    branchCode: string,
    opts?: RequestOptions
  ): Promise<AdminWriteResult> {
    const headers = buildAdminAuthHeaders(opts?.headers, bypassKey);
    return this.http.del<AdminWriteResult>(
      `/v3/admin/branches/${encodeURIComponent(branchCode)}`,
      { ...opts, headers }
    );
  }
}

class RegionsAdminApi {
  public constructor(private readonly http: ApiHttpClient) {}

  public upsert(
    bypassKey: string,
    body: RegionUpsertBody,
    opts?: RequestOptions
  ): Promise<AdminWriteResult> {
    const headers = buildAdminAuthHeaders(opts?.headers, bypassKey);
    return this.http.post<AdminWriteResult>('/v3/admin/regions', { ...opts, headers, body });
  }

  public remove(
    bypassKey: string,
    regionCode: string,
    opts?: RequestOptions
  ): Promise<AdminWriteResult> {
    const headers = buildAdminAuthHeaders(opts?.headers, bypassKey);
    return this.http.del<AdminWriteResult>(
      `/v3/admin/regions/${encodeURIComponent(regionCode)}`,
      { ...opts, headers }
    );
  }
}

class CoreDirectorCodesAdminApi {
  public constructor(private readonly http: ApiHttpClient) {}

  public upsert(
    bypassKey: string,
    body: CoreDirectorCodeUpsertBody,
    opts?: RequestOptions
  ): Promise<AdminWriteResult> {
    const headers = buildAdminAuthHeaders(opts?.headers, bypassKey);
    return this.http.post<AdminWriteResult>('/v3/admin/core-director-codes', { ...opts, headers, body });
  }

  public remove(
    bypassKey: string,
    domain: string,
    code: string,
    opts?: RequestOptions
  ): Promise<AdminWriteResult> {
    const headers = buildAdminAuthHeaders(opts?.headers, bypassKey);
    return this.http.del<AdminWriteResult>(
      `/v3/admin/core-director-codes/${encodeURIComponent(domain)}/${encodeURIComponent(code)}`,
      { ...opts, headers }
    );
  }
}

class NaicsCodesAdminApi {
  public constructor(private readonly http: ApiHttpClient) {}

  public upsert(
    bypassKey: string,
    body: NaicsCodeUpsertBody,
    opts?: RequestOptions
  ): Promise<AdminWriteResult> {
    const headers = buildAdminAuthHeaders(opts?.headers, bypassKey);
    return this.http.post<AdminWriteResult>('/v3/admin/naics-codes', { ...opts, headers, body });
  }

  public remove(
    bypassKey: string,
    code: string,
    opts?: RequestOptions
  ): Promise<AdminWriteResult> {
    const headers = buildAdminAuthHeaders(opts?.headers, bypassKey);
    return this.http.del<AdminWriteResult>(
      `/v3/admin/naics-codes/${encodeURIComponent(code)}`,
      { ...opts, headers }
    );
  }
}

class StateGovSearchLinksAdminApi {
  public constructor(private readonly http: ApiHttpClient) {}

  public upsert(
    bypassKey: string,
    body: StateGovSearchLinkUpsertBody,
    opts?: RequestOptions
  ): Promise<AdminWriteResult> {
    const headers = buildAdminAuthHeaders(opts?.headers, bypassKey);
    return this.http.post<AdminWriteResult>('/v3/admin/state-gov-search-links', { ...opts, headers, body });
  }

  public remove(
    bypassKey: string,
    stateCode: string,
    searchType: string,
    opts?: RequestOptions
  ): Promise<AdminWriteResult> {
    const headers = buildAdminAuthHeaders(opts?.headers, bypassKey);
    return this.http.del<AdminWriteResult>(
      `/v3/admin/state-gov-search-links/${encodeURIComponent(stateCode)}/${encodeURIComponent(searchType)}`,
      { ...opts, headers }
    );
  }
}

class AdminReferenceApi {
  public readonly branches: BranchesAdminApi;
  public readonly regions: RegionsAdminApi;
  public readonly coreDirectorCodes: CoreDirectorCodesAdminApi;
  public readonly naicsCodes: NaicsCodesAdminApi;
  public readonly stateGovSearchLinks: StateGovSearchLinksAdminApi;

  public constructor(http: ApiHttpClient) {
    this.branches = new BranchesAdminApi(http);
    this.regions = new RegionsAdminApi(http);
    this.coreDirectorCodes = new CoreDirectorCodesAdminApi(http);
    this.naicsCodes = new NaicsCodesAdminApi(http);
    this.stateGovSearchLinks = new StateGovSearchLinksAdminApi(http);
  }
}

class IraContributionPhaseoutsAdminApi {
  public constructor(private readonly http: ApiHttpClient) {}

  public upsert(
    bypassKey: string,
    body: IraContributionPhaseoutUpsertBody,
    opts?: RequestOptions
  ): Promise<AdminWriteResult> {
    const headers = buildAdminAuthHeaders(opts?.headers, bypassKey);
    return this.http.post<AdminWriteResult>('/v3/admin/ira-contribution-phaseouts', {
      ...opts,
      headers,
      body,
    });
  }

  public remove(
    bypassKey: string,
    taxYear: number,
    planCode: string,
    filingStatus: string,
    opts?: RequestOptions
  ): Promise<AdminWriteResult> {
    const headers = buildAdminAuthHeaders(opts?.headers, bypassKey);
    const path = `/v3/admin/ira-contribution-phaseouts/${encodeURIComponent(
      String(taxYear)
    )}/${encodeURIComponent(planCode)}/${encodeURIComponent(filingStatus)}`;
    return this.http.del<AdminWriteResult>(path, { ...opts, headers });
  }
}

// ── Wave 6.1 / Agent WRITE-ROUTES-SYSTEM-MISC helper classes ────────────

class SystemSettingsAdminApi {
  public constructor(private readonly http: ApiHttpClient) {}

  public upsert(
    bypassKey: string,
    body: SystemSettingUpsertBody,
    opts?: RequestOptions
  ): Promise<AdminWriteResult> {
    const headers = buildAdminAuthHeaders(opts?.headers, bypassKey);
    return this.http.post<AdminWriteResult>('/v3/admin/system-settings', { ...opts, headers, body });
  }

  public remove(
    bypassKey: string,
    settingKey: string,
    opts?: RequestOptions
  ): Promise<AdminWriteResult> {
    const headers = buildAdminAuthHeaders(opts?.headers, bypassKey);
    return this.http.del<AdminWriteResult>(
      `/v3/admin/system-settings/${encodeURIComponent(settingKey)}`,
      { ...opts, headers }
    );
  }
}

class KillListEntriesAdminApi {
  public constructor(private readonly http: ApiHttpClient) {}

  public upsert(
    bypassKey: string,
    body: KillListEntryUpsertBody,
    opts?: RequestOptions
  ): Promise<AdminWriteResult> {
    const headers = buildAdminAuthHeaders(opts?.headers, bypassKey);
    return this.http.post<AdminWriteResult>('/v3/admin/kill-list-entries', { ...opts, headers, body });
  }

  /**
   * BACKLOG-1757: read Core Director for one batch of entries and stamp each one's
   * "coreKillStatus". Changes no entry's status. Omit runStartedAt on the first call.
   */
  public coreStatus(
    bypassKey: string,
    body: { runStartedAt?: string; max?: number } = {},
    opts?: RequestOptions
  ): Promise<KillListCoreStatusResult> {
    const headers = buildAdminAuthHeaders(opts?.headers, bypassKey);
    return this.http.post<KillListCoreStatusResult>('/v3/admin/kill-list-entries/core-status', { ...opts, headers, body });
  }

  /** Soft-cancel (sets Status='cancelled'). The original row stays for audit. */
  public cancel(
    bypassKey: string,
    killListEntryId: string,
    opts?: RequestOptions
  ): Promise<AdminWriteResult> {
    const headers = buildAdminAuthHeaders(opts?.headers, bypassKey);
    return this.http.del<AdminWriteResult>(
      `/v3/admin/kill-list-entries/${encodeURIComponent(killListEntryId)}`,
      { ...opts, headers }
    );
  }
}

class SlaTargetsAdminApi {
  public constructor(private readonly http: ApiHttpClient) {}

  public upsert(
    bypassKey: string,
    body: SlaTargetUpsertBody,
    opts?: RequestOptions
  ): Promise<AdminWriteResult> {
    const headers = buildAdminAuthHeaders(opts?.headers, bypassKey);
    return this.http.post<AdminWriteResult>('/v3/admin/sla-targets', { ...opts, headers, body });
  }

  public remove(
    bypassKey: string,
    originCode: string,
    stageCode: string,
    opts?: RequestOptions
  ): Promise<AdminWriteResult> {
    const headers = buildAdminAuthHeaders(opts?.headers, bypassKey);
    const path = `/v3/admin/sla-targets/${encodeURIComponent(originCode)}/${encodeURIComponent(stageCode)}`;
    return this.http.del<AdminWriteResult>(path, { ...opts, headers });
  }
}

class CdPenaltyRulesAdminApi {
  public constructor(private readonly http: ApiHttpClient) {}

  public upsert(
    bypassKey: string,
    body: CdPenaltyRuleUpsertBody,
    opts?: RequestOptions
  ): Promise<AdminWriteResult> {
    const headers = buildAdminAuthHeaders(opts?.headers, bypassKey);
    return this.http.post<AdminWriteResult>('/v3/admin/cd-penalty-rules', { ...opts, headers, body });
  }

  public remove(
    bypassKey: string,
    ruleCode: string,
    opts?: RequestOptions
  ): Promise<AdminWriteResult> {
    const headers = buildAdminAuthHeaders(opts?.headers, bypassKey);
    return this.http.del<AdminWriteResult>(
      `/v3/admin/cd-penalty-rules/${encodeURIComponent(ruleCode)}`,
      { ...opts, headers }
    );
  }
}

class IraReasonCodesAdminApi {
  public constructor(private readonly http: ApiHttpClient) {}

  public upsert(
    bypassKey: string,
    body: IraReasonCodeUpsertBody,
    opts?: RequestOptions
  ): Promise<AdminWriteResult> {
    const headers = buildAdminAuthHeaders(opts?.headers, bypassKey);
    return this.http.post<AdminWriteResult>('/v3/admin/ira-reason-codes', { ...opts, headers, body });
  }

  public remove(
    bypassKey: string,
    code: string,
    opts?: RequestOptions
  ): Promise<AdminWriteResult> {
    const headers = buildAdminAuthHeaders(opts?.headers, bypassKey);
    return this.http.del<AdminWriteResult>(
      `/v3/admin/ira-reason-codes/${encodeURIComponent(code)}`,
      { ...opts, headers }
    );
  }
}

class IraContributionLimitsAdminApi {
  public constructor(private readonly http: ApiHttpClient) {}

  public upsert(
    bypassKey: string,
    body: IraContributionLimitUpsertBody,
    opts?: RequestOptions
  ): Promise<AdminWriteResult> {
    const headers = buildAdminAuthHeaders(opts?.headers, bypassKey);
    return this.http.post<AdminWriteResult>('/v3/admin/ira-contribution-limits', { ...opts, headers, body });
  }

  public remove(
    bypassKey: string,
    taxYear: number,
    planCode: string,
    opts?: RequestOptions
  ): Promise<AdminWriteResult> {
    const headers = buildAdminAuthHeaders(opts?.headers, bypassKey);
    const path = `/v3/admin/ira-contribution-limits/${encodeURIComponent(
      String(taxYear)
    )}/${encodeURIComponent(planCode)}`;
    return this.http.del<AdminWriteResult>(path, { ...opts, headers });
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Wave 8 NOTIFICATIONS-SPINE — typed write surfaces for event bindings + opt-outs.
// Routes: POST   /v3/admin/notification-event-bindings        (createOrUpdate)
//         DELETE /v3/admin/notification-event-bindings/{eventCode}
//         POST   /v3/admin/notification-opt-outs              (createOrUpdate)
//         DELETE /v3/admin/notification-opt-outs/{id}
// All require `x-config-seed-bypass: <bypassKey>`.
// ─────────────────────────────────────────────────────────────────────────────

class NotificationEventBindingsAdminApi {
  public constructor(private readonly http: ApiHttpClient) {}

  public upsert(
    bypassKey: string,
    body: NotifEventBindingUpsert,
    opts?: RequestOptions
  ): Promise<AdminWriteResult> {
    const headers = buildAdminAuthHeaders(opts?.headers, bypassKey);
    return this.http.post<AdminWriteResult>('/v3/admin/notification-event-bindings', {
      ...opts,
      headers,
      body,
    });
  }

  public remove(
    bypassKey: string,
    eventCode: string,
    opts?: RequestOptions
  ): Promise<AdminWriteResult> {
    const headers = buildAdminAuthHeaders(opts?.headers, bypassKey);
    return this.http.del<AdminWriteResult>(
      `/v3/admin/notification-event-bindings/${encodeURIComponent(eventCode)}`,
      { ...opts, headers }
    );
  }
}

class NotificationOptOutsAdminApi {
  public constructor(private readonly http: ApiHttpClient) {}

  public upsert(
    bypassKey: string,
    body: NotifOptOutUpsert,
    opts?: RequestOptions
  ): Promise<AdminWriteResult> {
    const headers = buildAdminAuthHeaders(opts?.headers, bypassKey);
    return this.http.post<AdminWriteResult>('/v3/admin/notification-opt-outs', {
      ...opts,
      headers,
      body,
    });
  }

  /**
   * Restore (un-suppress) a soft opt-out. The backend has NO DELETE route for
   * opt-outs — the restore is POST /v3/admin/notification-opt-outs/{id}/restore
   * (sets RestoredAt). The prior `remove()` issued a DELETE that 404'd, so the
   * editor's Restore action was dead (BACKLOG-0579 verification fix 2026-06-17).
   */
  public restore(
    bypassKey: string,
    id: string,
    opts?: RequestOptions
  ): Promise<AdminWriteResult> {
    const headers = buildAdminAuthHeaders(opts?.headers, bypassKey);
    return this.http.post<AdminWriteResult>(
      `/v3/admin/notification-opt-outs/${encodeURIComponent(id)}/restore`,
      { ...opts, headers }
    );
  }
}

/**
 * Generic admin-write surface adapter. Used as a placeholder for admin
 * sub-apis whose backend routes exist server-side but haven't yet been
 * wrapped in a typed class. The class exposes a small set of CRUD verbs
 * plus an index signature so editor code can call any method name (e.g.
 * `generateSample`, `archivalSweep`, `testModeCounts`) without TS errors;
 * the call routes to a POST under the surface's base path. A typed class
 * can replace the generic surface in-place when one is added.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyAsyncFn = (...args: any[]) => Promise<any>;

/* eslint-disable @typescript-eslint/no-explicit-any */
class GenericAdminSurfaceBase {
  public constructor(
    public readonly http: ApiHttpClient,
    public readonly basePath: string,
  ) {}

  public list(query?: Record<string, unknown>, _opts?: unknown): Promise<any> {
    return this.http.get(this.basePath, query ? { query: query as Record<string, string | number | boolean | undefined> } : undefined);
  }
  public upsert(body?: unknown, _opts?: unknown): Promise<any> {
    const headers = buildAdminAuthHeaders(undefined, undefined);
    return this.http.post(`${this.basePath}/upsert`, { headers, body });
  }
  public remove(keyOrCohort: string, tileId?: string, _opts?: unknown): Promise<any> {
    const headers = buildAdminAuthHeaders(undefined, undefined);
    const key = tileId
      ? `${encodeURIComponent(keyOrCohort)}/${encodeURIComponent(tileId)}`
      : encodeURIComponent(keyOrCohort);
    return this.http.del(`${this.basePath}/${key}`, { headers });
  }
  public reorder(body?: unknown, _opts?: unknown): Promise<any> {
    const headers = buildAdminAuthHeaders(undefined, undefined);
    return this.http.post(`${this.basePath}/reorder`, { headers, body });
  }
}
/* eslint-enable @typescript-eslint/no-explicit-any */

/**
 * Public type alias — combines the typed CRUD class with an index signature
 * so editors that call ad-hoc verb names (per-surface JSON-RPC style) keep
 * compiling. Each unknown verb resolves to a POST against
 * `{basePath}/{verb}` carrying the call's first argument as the body.
 */
export type GenericAdminSurface = GenericAdminSurfaceBase & {
  [verb: string]: AnyAsyncFn;
};

function makeGenericAdminSurface(http: ApiHttpClient, basePath: string): GenericAdminSurface {
  const base = new GenericAdminSurfaceBase(http, basePath);
  return new Proxy(base, {
    get(target, prop, receiver) {
      const existing = Reflect.get(target, prop, receiver);
      if (typeof existing !== 'undefined') return existing;
      if (typeof prop !== 'string') return existing;
      // Unknown verb — route to POST /{basePath}/{verb}.
      const verb = prop;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return async (body?: unknown): Promise<any> => {
        const headers = buildAdminAuthHeaders(undefined, undefined);
        // kebab-case the verb (generateSample → generate-sample) so the
        // server route table stays conventional.
        const slug = verb.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`);
        return target.http.post(`${basePath}/${slug}`, { headers, body });
      };
    },
  }) as unknown as GenericAdminSurface;
}

export class AdminApi {
  public readonly iraTransactionCodes: IraTransactionCodesAdminApi;
  public readonly iraInstitutionRelationships: IraInstitutionRelationshipsAdminApi;
  public readonly iraContributionPhaseouts: IraContributionPhaseoutsAdminApi;
  public readonly reference: AdminReferenceApi;

  // Wave 6.1 — System / misc typed write surfaces.
  public readonly systemSettings: SystemSettingsAdminApi;
  public readonly killListEntries: KillListEntriesAdminApi;
  public readonly slaTargets: SlaTargetsAdminApi;
  public readonly cdPenaltyRules: CdPenaltyRulesAdminApi;
  public readonly iraReasonCodes: IraReasonCodesAdminApi;
  public readonly iraContributionLimits: IraContributionLimitsAdminApi;

  // Wave 8 — Notifications spine admin surfaces.
  public readonly notificationEventBindings: NotificationEventBindingsAdminApi;
  public readonly notificationOptOuts: NotificationOptOutsAdminApi;

  // ── Type-safety surfaces backed by routes shipped server-side ────────────
  // Editors below expect these namespaces. Each is a thin GenericAdminSurface
  // so editor code compiles; the backend route table is the canonical
  // contract. When a typed class is added (see e.g. AdminWizardDocMatrixApi)
  // it can replace the generic surface in-place without touching callers.
  public readonly dataMaintenance: GenericAdminSurface;
  public readonly users: GenericAdminSurface;
  public readonly parmValSync: GenericAdminSurface;
  public readonly retentionHolds: GenericAdminSurface;
  public readonly signingEngineRouting: GenericAdminSurface;
  public readonly wizardDocMatrix: GenericAdminSurface;
  /** Domain 1.6b — typed admin CRUD for dbo.Roles (upsert + remove). */
  public readonly roles: GenericAdminSurface;
  /** Domain 1.6b — typed admin CRUD for dbo.UserRoleAssignments. */
  public readonly userRoleAssignments: GenericAdminSurface;
  /** alpha.78 — jXchange diagnostic probe surface (POST /v3/admin/jxchange/probe). */
  public readonly jxchange: GenericAdminSurface;

  public constructor(private readonly http: ApiHttpClient) {
    this.iraTransactionCodes = new IraTransactionCodesAdminApi(http);
    this.iraInstitutionRelationships = new IraInstitutionRelationshipsAdminApi(http);
    this.iraContributionPhaseouts = new IraContributionPhaseoutsAdminApi(http);
    this.reference = new AdminReferenceApi(http);
    this.systemSettings = new SystemSettingsAdminApi(http);
    this.killListEntries = new KillListEntriesAdminApi(http);
    this.slaTargets = new SlaTargetsAdminApi(http);
    this.cdPenaltyRules = new CdPenaltyRulesAdminApi(http);
    this.iraReasonCodes = new IraReasonCodesAdminApi(http);
    this.iraContributionLimits = new IraContributionLimitsAdminApi(http);
    this.notificationEventBindings = new NotificationEventBindingsAdminApi(http);
    this.notificationOptOuts = new NotificationOptOutsAdminApi(http);
    this.dataMaintenance = makeGenericAdminSurface(http, '/v3/admin/data-maintenance');
    this.users = makeGenericAdminSurface(http, '/v3/admin/users');
    this.parmValSync = makeGenericAdminSurface(http, '/v3/admin/parmval-sync');
    this.retentionHolds = makeGenericAdminSurface(http, '/v3/admin/retention-holds');
    this.signingEngineRouting = makeGenericAdminSurface(http, '/v3/admin/signing-engine-routing');
    this.wizardDocMatrix = makeGenericAdminSurface(http, '/v3/admin/wizard-doc-matrix');
    this.roles = makeGenericAdminSurface(http, '/v3/admin/roles');
    this.userRoleAssignments = makeGenericAdminSurface(http, '/v3/admin/user-role-assignments');
    this.jxchange = makeGenericAdminSurface(http, '/v3/admin/jxchange');
  }

  public configSeed(
    req: ConfigSeedRequest,
    opts?: RequestOptions
  ): Promise<ConfigSeedResponse> {
    return this.http.post<ConfigSeedResponse>('/v3/admin/config-seed', { ...opts, body: req });
  }

  /**
   * Execute an admin-approved SQL batch against pcb-retail-ops.
   *
   * Requires:
   *   - `x-functions-key` (injected by http transport)
   *   - `x-config-seed-bypass: <bypassKey>` — passed as the first positional
   *     arg and forwarded as an extra request header.
   *
   * The endpoint splits on `GO` batch separators, so MERGE + verification
   * SELECT scripts work. Use `sqlBase64` when SQL contains multi-byte chars.
   */
  public runSeedSql(
    bypassKey: string,
    req: RunSeedSqlRequest,
    opts?: RequestOptions
  ): Promise<RunSeedSqlResponse> {
    const headers = buildAdminAuthHeaders(opts?.headers, bypassKey);
    return this.http.post<RunSeedSqlResponse>('/v3/admin/run-seed-sql', {
      ...opts,
      headers,
      body: req,
    });
  }

  /**
   * Domain 1.6a — Generic Entra group picker. Returns groups whose
   * displayName starts with `query` (case-insensitive). Empty query returns
   * the first `top` groups alphabetically. Use this to drive the Roles
   * editor's GroupId picker (when GroupType ≠ 'Manual') and the
   * SystemSettings editor for any `*.groupId` setting.
   */
  public searchEntraGroups(
    query: string | undefined,
    top: number = 10,
    opts?: RequestOptions
  ): Promise<EntraSearchEnvelope<EntraGroupCandidate>> {
    const qs = new URLSearchParams();
    if (query) qs.set('q', query);
    qs.set('top', String(top));
    return this.http.get<EntraSearchEnvelope<EntraGroupCandidate>>(
      `/v3/admin/entra/groups?${qs.toString()}`,
      opts
    );
  }

  /**
   * Domain 1.6a — Add-user autocomplete. Returns members of the canonical
   * identity source group (SystemSetting `identity.source.groupId`, default
   * = AllOwners) whose displayName starts with `query`, AFTER excluding
   * users already in `dbo.Users`. Use this in the admin-panel Add User
   * modal's name search field.
   */
  public searchEntraCandidates(
    query: string | undefined,
    top: number = 20,
    opts?: RequestOptions
  ): Promise<EntraSearchEnvelope<EntraUserCandidate>> {
    const qs = new URLSearchParams();
    if (query) qs.set('q', query);
    qs.set('top', String(top));
    return this.http.get<EntraSearchEnvelope<EntraUserCandidate>>(
      `/v3/admin/users/entra-candidates?${qs.toString()}`,
      opts
    );
  }

  // ── v0.8.0 — Bill 2026-05-15 ────────────────────────────────────────

  /** Diagnostic — caller's resolved identity (bearer OID + officer-code). */
  public authProbe(opts?: RequestOptions): Promise<AuthProbeResponse> {
    return this.http.get<AuthProbeResponse>('/v3/admin/auth-probe', opts);
  }

  /** Per-user Entra sync — diff only, no apply. */
  public syncSingleUserDiff(
    userId: number,
    opts?: RequestOptions,
  ): Promise<{ success: boolean; data: SingleUserDiffResult; error?: string; code?: string }> {
    return this.http.post<{ success: boolean; data: SingleUserDiffResult; error?: string; code?: string }>(
      `/v3/admin/users/${userId}/sync-from-entra`, { ...opts, body: {} },
    );
  }

  /** Per-user Entra sync — apply accepted fields. */
  public syncSingleUserApply(
    userId: number,
    body: SingleSyncApplyBody,
    opts?: RequestOptions,
  ): Promise<{ success: boolean; data: SingleUserDiffResult; error?: string; code?: string }> {
    return this.http.post<{ success: boolean; data: SingleUserDiffResult; error?: string; code?: string }>(
      `/v3/admin/users/${userId}/sync-from-entra?apply=true`, { ...opts, body },
    );
  }

  /** Refresh user's Entra photo (writes to dbo.Users.PhotoBase64). */
  public fetchUserEntraPhoto(userId: number, opts?: RequestOptions): Promise<UserPhotoResponse> {
    return this.http.get<UserPhotoResponse>(`/v3/admin/users/${userId}/entra-photo`, opts);
  }

  /** SyncTab Reconcile mode source (per-user field deltas). */
  public syncReconcile(opts?: RequestOptions): Promise<{ success: boolean; data: ReconcileResult }> {
    return this.http.post<{ success: boolean; data: ReconcileResult }>(
      '/v3/admin/users/entra-sync/reconcile', { ...opts, body: {} },
    );
  }

  /** SyncTab Global Suggest mode source (per-role group scan). */
  public syncGlobalSuggest(opts?: RequestOptions): Promise<{ success: boolean; data: GlobalSuggestResult }> {
    return this.http.post<{ success: boolean; data: GlobalSuggestResult }>(
      '/v3/admin/users/entra-sync/global-suggest', { ...opts, body: {} },
    );
  }

  /**
   * v0.8.2 Global Suggest apply — writes ALL Entra columns to dbo.Users
   * and inserts the role assignment from the originating section. Archives
   * revoke the assignment (user stays active).
   */
  public syncGlobalSuggestApply(
    body: {
      addsByRole: ReadonlyArray<{ roleCode: string; candidates: ReadonlyArray<EntraUserRow> }>;
      archivesByRole: ReadonlyArray<{ roleCode: string; userIds: ReadonlyArray<number> }>;
    },
    opts?: RequestOptions,
  ): Promise<{
    success: boolean;
    data: {
      addedUserCount: number;
      updatedUserCount: number;
      addedAssignmentCount: number;
      archivedAssignmentCount: number;
      elapsedMs: number;
      errors: ReadonlyArray<string>;
    };
  }> {
    return this.http.post('/v3/admin/users/entra-sync/global-suggest/apply', { ...opts, body });
  }

  /** v0.8.0 maintenance — backfill EntraObjectId for users currently NULL. */
  public backfillEntraOids(opts?: RequestOptions): Promise<{
    success: boolean;
    data: {
      scanned: number;
      matched: number;
      updated: number;
      failures: ReadonlyArray<{ userId: number; email: string; reason: string }>;
    };
  }> {
    return this.http.post('/v3/admin/users/backfill-entra-oids', { ...opts, body: {} });
  }

  // ── 3 ignore/override CRUD surfaces ─────────────────────────────────
  public listFieldSyncIgnores(userId?: number, opts?: RequestOptions): Promise<{ success: boolean; data: ReadonlyArray<FieldIgnoreRow> }> {
    const qs = userId ? `?userId=${userId}` : '';
    return this.http.get(`/v3/admin/users/field-sync-ignores${qs}`, opts);
  }
  public createFieldSyncIgnore(body: FieldIgnoreInput, opts?: RequestOptions): Promise<{ success: boolean; data: { ignoreId: number } }> {
    return this.http.post('/v3/admin/users/field-sync-ignores', { ...opts, body });
  }
  public deleteFieldSyncIgnore(id: number, opts?: RequestOptions): Promise<{ success: boolean }> {
    return this.http.del(`/v3/admin/users/field-sync-ignores/${id}`, opts);
  }

  public listGroupSyncIgnores(opts?: RequestOptions): Promise<{ success: boolean; data: ReadonlyArray<GroupIgnoreRowV08> }> {
    return this.http.get('/v3/admin/users/group-sync-ignores', opts);
  }
  public createGroupSyncIgnore(body: GroupIgnoreInputV08, opts?: RequestOptions): Promise<{ success: boolean; data: { ignoreId: string } }> {
    return this.http.post('/v3/admin/users/group-sync-ignores', { ...opts, body });
  }
  public deleteGroupSyncIgnore(id: string, opts?: RequestOptions): Promise<{ success: boolean }> {
    return this.http.del(`/v3/admin/users/group-sync-ignores/${id}`, opts);
  }

  public listSubordinateOverrides(supervisorUserId?: number, opts?: RequestOptions): Promise<{ success: boolean; data: ReadonlyArray<SubordinateOverrideRow> }> {
    const qs = supervisorUserId ? `?supervisorUserId=${supervisorUserId}` : '';
    return this.http.get(`/v3/admin/users/subordinate-overrides${qs}`, opts);
  }
  public createSubordinateOverride(body: SubordinateOverrideInput, opts?: RequestOptions): Promise<{ success: boolean; data: { overrideId: number } }> {
    return this.http.post('/v3/admin/users/subordinate-overrides', { ...opts, body });
  }
  public deleteSubordinateOverride(id: number, opts?: RequestOptions): Promise<{ success: boolean }> {
    return this.http.del(`/v3/admin/users/subordinate-overrides/${id}`, opts);
  }

  /**
   * Domain 1.7 — lists active users currently flagged with
   * `MissingOfficerCode=true`. SysOps queue source.
   */
  public listMissingOfficerCode(
    opts?: RequestOptions
  ): Promise<MissingOfficerCodeListResponse> {
    return this.http.get<MissingOfficerCodeListResponse>(
      '/v3/admin/users/missing-officer-code',
      opts
    );
  }

  // ─── v0.8.5 Core Director Sync ─────────────────────────────────────
  // Live ParmValSrch fetch + fuzzy match against v3 DisplayName. Reconcile
  // is preview-only; Apply writes per accepted bucket (assigns / stale
  // null-outs / name-mismatch acks).

  public cdSyncReconcile(opts?: RequestOptions): Promise<CdSyncReconcileResponse> {
    return this.http.post<CdSyncReconcileResponse>(
      '/v3/admin/users/cd-sync/reconcile',
      { ...opts, body: {} },
    );
  }

  public cdSyncApply(body: CdSyncApplyInput, opts?: RequestOptions): Promise<CdSyncApplyResponse> {
    return this.http.post<CdSyncApplyResponse>(
      '/v3/admin/users/cd-sync/apply',
      { ...opts, body },
    );
  }

  /** v0.8.5 SysOps queue — users flagged with `CdNameMismatch=true`. */
  public listCdNameMismatch(opts?: RequestOptions): Promise<CdNameMismatchListResponse> {
    return this.http.get<CdNameMismatchListResponse>(
      '/v3/admin/users/cd-name-mismatch',
      opts,
    );
  }

  /** v0.8.5 SysOps action — clear CdNameMismatch after fixing CD descriptor. */
  public resolveCdNameMismatch(userId: number, opts?: RequestOptions): Promise<ResolveCdNameMismatchResponse> {
    return this.http.post<ResolveCdNameMismatchResponse>(
      `/v3/admin/users/${userId}/resolve-cd-name-mismatch`,
      { ...opts, body: {} },
    );
  }

  // ─── v0.9.3 Operator Catalog Issues queue ───────────────────────────
  public listOperatorCatalogIssues(opts?: RequestOptions): Promise<OperatorCatalogIssuesListResponse> {
    return this.http.get<OperatorCatalogIssuesListResponse>(
      '/v3/admin/operator-catalog-issues',
      opts,
    );
  }
  public resolveOperatorCatalogIssue(
    issueId: number,
    body: { notes?: string } = {},
    opts?: RequestOptions,
  ): Promise<ResolveOperatorCatalogIssueResponse> {
    return this.http.post<ResolveOperatorCatalogIssueResponse>(
      `/v3/admin/operator-catalog-issues/${issueId}/resolve`,
      { ...opts, body },
    );
  }

  // ─── v0.9.1 SharePoint Core Operator Catalog ──────────────────────────
  public refreshCoreOperatorCatalog(opts?: RequestOptions): Promise<CoreOperatorCatalogRefreshResponse> {
    return this.http.post<CoreOperatorCatalogRefreshResponse>(
      '/v3/admin/core-operator-catalog/refresh',
      { ...opts, body: {} },
    );
  }
  public listCoreOperatorCatalog(opts?: RequestOptions): Promise<CoreOperatorCatalogListResponse> {
    return this.http.get<CoreOperatorCatalogListResponse>(
      '/v3/admin/core-operator-catalog',
      opts,
    );
  }

  // ─── v0.9.2 Core Director branch sync ────────────────────────────────
  public branchesCdSync(opts?: RequestOptions): Promise<{
    success: boolean;
    error?: string;
    code?: string;
    data?: {
      cdRecordCount: number;
      inserted: number;
      updated: number;
      renamed: number;
      cdNotInV3: number;
      entraMappedCount: number;
      elapsedMs: number;
      diffs: ReadonlyArray<{ cdCode: string; cdName: string; v3Code: string | null; v3Name: string | null; action: string }>;
      entraMappings: ReadonlyArray<{ entraValue: string; userCount: number; matchedBranchCode: string | null; matchedBranchName: string | null; applied: boolean }>;
    };
  }> {
    return this.http.post('/v3/admin/branches/cd-sync', { ...opts, body: {} });
  }

  // ─── v0.9.0 product-category enablement ──────────────────────────────
  public listUserProductCategories(userId: number, opts?: RequestOptions): Promise<UserProductCategoriesResponse> {
    return this.http.get<UserProductCategoriesResponse>(
      `/v3/admin/users/${userId}/product-categories`,
      opts,
    );
  }
  /** GET /v3/admin/users/{userId}/access-history — newest first (Admin). */
  public userAccessHistory(userId: number, opts?: RequestOptions): Promise<UserAccessHistoryResponse> {
    return this.http.get<UserAccessHistoryResponse>(
      `/v3/admin/users/${userId}/access-history`,
      opts,
    );
  }
  public replaceUserProductCategories(userId: number, body: UserProductCategoriesReplaceInput, opts?: RequestOptions): Promise<UserProductCategoriesReplaceResponse> {
    return this.http.post<UserProductCategoriesReplaceResponse>(
      `/v3/admin/users/${userId}/product-categories`,
      { ...opts, body },
    );
  }

  /**
   * Domain 1.6c — preview Entra/SQL sync. Returns adds/updates/removes
   * without writing. Pass `groupObjectId` (Entra group OID) to scope; omit
   * for the full-tenant default (or use env-configured default).
   * Server gate: SuperAdmin.
   */
  public syncDiff(
    req: EntraSyncDiffRequest,
    opts?: RequestOptions,
  ): Promise<{ success: boolean; data: EntraSyncDiffOutcome }> {
    return this.http.post<{ success: boolean; data: EntraSyncDiffOutcome }>(
      '/v3/admin/users/entra-sync/diff',
      { ...opts, body: req },
    );
  }

  /**
   * Domain 1.6c — apply admin-accepted diff buckets. Anything omitted is
   * left untouched. Server gate: SuperAdmin.
   */
  public syncApply(
    req: EntraSyncApplyInput,
    opts?: RequestOptions,
  ): Promise<{ success: boolean; data: EntraSyncApplyOutcome }> {
    return this.http.post<{ success: boolean; data: EntraSyncApplyOutcome }>(
      '/v3/admin/users/entra-sync/apply',
      { ...opts, body: req },
    );
  }

  /**
   * Domain 1.6c — admin-curated "stop proposing this Entra user" list.
   * Server gate: SuperAdmin.
   */
  public syncIgnoreList(opts?: RequestOptions): Promise<EntraIgnoreListResponse> {
    return this.http.get<EntraIgnoreListResponse>(
      '/v3/admin/users/entra-sync/ignore-list',
      opts,
    );
  }

  public syncIgnore(
    req: EntraIgnoreInput,
    opts?: RequestOptions,
  ): Promise<{ success: boolean }> {
    return this.http.post<{ success: boolean }>(
      '/v3/admin/users/entra-sync/ignore',
      { ...opts, body: req },
    );
  }

  public syncIgnoreBulk(
    req: EntraIgnoreBulkInput,
    opts?: RequestOptions,
  ): Promise<{ success: boolean; data: { inserted: number } }> {
    return this.http.post<{ success: boolean; data: { inserted: number } }>(
      '/v3/admin/users/entra-sync/ignore-bulk',
      { ...opts, body: req },
    );
  }

  public syncUnignore(
    id: number,
    opts?: RequestOptions,
  ): Promise<{ success: boolean }> {
    return this.http.del<{ success: boolean }>(
      `/v3/admin/users/entra-sync/ignore/${id}`,
      opts,
    );
  }

  /**
   * Domain 1.7 — clears `MissingOfficerCode` after validating the supplied
   * code against Core Director's OffCode catalog. Returns 422
   * INVALID_OFFICER_CODE when the code isn't in the catalog.
   */
  public resolveOfficerCode(
    userId: number,
    req: ResolveOfficerCodeRequest,
    opts?: RequestOptions
  ): Promise<ResolveOfficerCodeResponse> {
    return this.http.post<ResolveOfficerCodeResponse>(
      `/v3/admin/users/${userId}/resolve-officer-code`,
      { ...opts, body: req }
    );
  }
}
